import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Button,
  Chip,
  Tooltip,
  Alert,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../api/client";
import { getConversationById } from "../../api/client";
import { useRequests } from "../../hooks/useRequests";
import StatusChipSelector, { normalizeStatusInput } from "../StatusChipSelector";
import {
  buildAgentSimulatorTurnMessages,
  buildSimulationTransferSystemMessage,
  buildSimulationSkipSystemMessage,
  resolveSimulatorQuickReplyPayload,
} from "../../utils/agentSimulatorMessages";
import AgentSimulatorAttachment from "./AgentSimulatorAttachment";
import SimulatorTypingBubble from "../ChatbotFlow/SimulatorTypingBubble";
import { resolveSimulationProjectId } from "../../utils/primaryProjectId";

export default function AgentSimulator({
  agentId,
  agentName,
  primarySkillKey = null,
  agentActive = true,
}) {
  const { t } = useTranslation();
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState({ x: 32, y: 80 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [requestStatus, setRequestStatus] = useState(null);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [transferred, setTransferred] = useState(false);

  const { updateMutation } = useRequests();
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const sendingRef = useRef(false);

  useEffect(() => {
    sendingRef.current = sending;
  }, [sending]);

  const computeInitialPosition = () => {
    try {
      const w = window.innerWidth || 0;
      const h = window.innerHeight || 0;
      const width = 320;
      const height = 60;
      const margin = 20;
      const x = Math.max(margin, w - width - margin);
      const y = Math.max(64, h - height - margin);
      return { x, y };
    } catch {
      return { x: 32, y: 80 };
    }
  };

  useEffect(() => {
    setPosition(computeInitialPosition());
  }, []);

  useEffect(() => {
    if (!expanded) return undefined;
    const container = messagesContainerRef.current;
    if (!container) return undefined;
    const scrollToLatest = () => {
      container.scrollTop = container.scrollHeight;
    };
    scrollToLatest();
    const frame = requestAnimationFrame(scrollToLatest);
    return () => cancelAnimationFrame(frame);
  }, [messages, expanded, sending]);

  const protocol = useMemo(() => (sessionId ? String(sessionId) : ""), [sessionId]);
  const isClosedRequest = useMemo(() => {
    const s = normalizeStatusInput(requestStatus);
    return s === 2 || s === 4 || s === 5;
  }, [requestStatus]);
  const inputLocked = isClosedRequest || transferred;

  const focusMessageInput = useCallback(() => {
    if (!expanded || inputLocked) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        inputRef.current?.focus?.();
      });
    });
  }, [expanded, inputLocked]);

  const refreshRequestDetails = async (id) => {
    if (!id) return;
    try {
      setLoadingRequest(true);
      const dto = await getConversationById(id);
      const status = dto?.status ?? dto?.data?.status ?? null;
      if (status !== null && status !== undefined) {
        setRequestStatus(normalizeStatusInput(status));
      }
    } catch (err) {
      console.error("[AgentSimulator] Erro ao buscar detalhes da solicitação:", err);
    } finally {
      setLoadingRequest(false);
    }
  };

  useEffect(() => {
    if (!sessionId) {
      setRequestStatus(null);
      return;
    }
    refreshRequestDetails(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const openPopupCall = () => {
    if (!sessionId) return;
    const width = window.screen.width;
    const height = window.screen.height;
    window.open(
      `/popup-call/${sessionId}`,
      "SolicitacaoPopup",
      `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
    );
  };

  const handleStatusChange = (newStatus) => {
    if (!sessionId) return;
    updateMutation.mutate(
      { id: sessionId, status: newStatus },
      {
        onSuccess: async () => {
          setRequestStatus(normalizeStatusInput(newStatus));
          await refreshRequestDetails(sessionId);
        },
        onError: (err) => {
          const msg =
            err?.message ||
            err?.error ||
            (Array.isArray(err?.message) && err.message[0]) ||
            "Erro ao atualizar status da solicitação.";
          setMessages((prev) => [...prev, { from: "system", text: msg }]);
        },
      }
    );
  };

  const sendMessage = async (text) => {
    const trimmed = String(text || "").trim();
    if (!trimmed || !agentId) return;
    if (inputLocked) return;
    if (sendingRef.current) return;

    setMessages((prev) => [...prev, { from: "user", text: trimmed, pending: true }]);
    setSending(true);

    try {
      let sid = sessionId;
      if (!sid) {
        try {
          const res = await fetchData(`/agents/${agentId}/simulation/sessions`, "POST", {
            id_projeto: resolveSimulationProjectId() || undefined,
          });
          const data = res?.data ?? res ?? {};
          sid = data.sessionId;
        } catch (createErr) {
          const stuckId = Number(createErr?.data?.id ?? createErr?.id) || 0;
          if (createErr?.status === 409 && stuckId) {
            try {
              await fetchData(`/agents/${agentId}/simulation/sessions/${stuckId}`, "DELETE");
            } catch {
              /* ignore */
            }
            try {
              const retry = await fetchData(`/agents/${agentId}/simulation/sessions`, "POST", {
                id_projeto: resolveSimulationProjectId() || undefined,
              });
              const retryData = retry?.data ?? retry ?? {};
              sid = retryData.sessionId || stuckId;
            } catch {
              sid = stuckId;
            }
          } else {
            throw createErr;
          }
        }
        setSessionId(sid);
        await refreshRequestDetails(sid);
      }

      const resMsg = await fetchData(`/agents/${agentId}/simulation/sessions/${sid}/messages`, "POST", {
        message: trimmed,
      });
      const dataMsg = resMsg?.data ?? resMsg ?? {};
      const turnMessages = buildAgentSimulatorTurnMessages(dataMsg, trimmed);
      const botMessages = turnMessages.filter((m) => m.from !== "user");
      const transferNotice = buildSimulationTransferSystemMessage(dataMsg, t);
      const skipNotice = buildSimulationSkipSystemMessage(dataMsg, t);
      if (dataMsg?.transferred || dataMsg?.transfer?.transferred) {
        setTransferred(true);
        setInput("");
      }

      setMessages((prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i -= 1) {
          if (next[i]?.from === "user" && next[i]?.pending && next[i]?.text === trimmed) {
            next[i] = { ...next[i], pending: false };
            break;
          }
        }
        const extras = [
          ...botMessages,
          ...(skipNotice ? [skipNotice] : []),
          ...(transferNotice ? [transferNotice] : []),
        ];
        return extras.length ? [...next, ...extras] : next;
      });
    } catch (err) {
      const stuckId = Number(err?.data?.id ?? err?.id) || 0;
      if (err?.status === 409 && stuckId && !sessionId) {
        setSessionId(stuckId);
        await refreshRequestDetails(stuckId);
      }
      const msg =
        err?.message ||
        err?.error ||
        (Array.isArray(err?.message) && err.message[0]) ||
        "Erro ao simular mensagem.";
      const hint =
        err?.status === 409 && stuckId
          ? ` Protocolo ${stuckId} — use Limpar ou altere o status para liberar.`
          : "";
      setMessages((prev) => {
        const next = prev.map((m) =>
          m?.pending && m.from === "user" && m.text === trimmed ? { ...m, pending: false } : m
        );
        return [...next, { from: "system", text: `${msg}${hint}` }];
      });
    } finally {
      setSending(false);
      focusMessageInput();
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !agentId) return;
    setInput("");
    await sendMessage(text);
  };

  const handleReset = async () => {
    const sid = sessionId;
    if (sid && agentId) {
      try {
        await fetchData(`/agents/${agentId}/simulation/sessions/${sid}`, "DELETE");
      } catch (err) {
        console.error("[AgentSimulator] Erro ao excluir sessão de simulação:", err);
      }
    }
    setSessionId(null);
    setMessages([]);
    setInput("");
    setRequestStatus(null);
    setTransferred(false);
  };

  const handleQuickReplyClick = async (label, optionId = "") => {
    if (sending || inputLocked) return;
    const sendValue = resolveSimulatorQuickReplyPayload(label, optionId);
    if (!sendValue) return;
    await sendMessage(sendValue);
  };

  const handleMouseDown = (e) => {
    if (e.target instanceof HTMLElement && e.target.closest("button, input, textarea, a")) {
      return;
    }
    e.preventDefault();
    setDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleDragMove = (clientX, clientY) => {
    setPosition({
      x: Math.max(8, clientX - offset.x),
      y: Math.max(64, clientY - offset.y),
    });
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      e.preventDefault();
      handleDragMove(e.clientX, e.clientY);
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, offset.x, offset.y]);

  const displayAgentName = agentName ? String(agentName).trim() : "Agente de IA";
  const showTicketSellerShortcuts = primarySkillKey === "ticket_seller";

  return (
    <Paper
      elevation={1}
      sx={{
        position: "fixed",
        zIndex: 1100,
        top: position.y,
        right: "auto",
        left: position.x,
        p: 1.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        width: 320,
        maxWidth: "90vw",
        height: expanded ? 420 : "auto",
        maxHeight: 480,
        overflow: expanded ? "hidden" : "visible",
        cursor: dragging ? "grabbing" : "default",
        boxShadow: (t) => t.shadows[12],
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 1,
          cursor: "grab",
          userSelect: dragging ? "none" : "auto",
          gap: 1,
        }}
        onMouseDown={handleMouseDown}
      >
        <DragIndicatorIcon fontSize="small" sx={{ color: "text.secondary" }} />
        <SmartToyOutlinedIcon fontSize="small" sx={{ color: "primary.main" }} />
        <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ maxWidth: 140 }}>
          Testar agente
        </Typography>
        <Button
          size="small"
          onClick={handleReset}
          variant={transferred ? "contained" : "text"}
          sx={{ ml: "auto", textTransform: "none" }}
          disabled={!messages.length && !sessionId}
        >
          Limpar
        </Button>
        <Button
          size="small"
          onClick={() => {
            setExpanded((prev) => {
              const next = !prev;
              if (!prev && next) {
                try {
                  const h = window.innerHeight || 0;
                  const popupHeight = 420;
                  const margin = 16;
                  const targetY = Math.max(64, h - popupHeight - margin);
                  setPosition((p) => ({ ...p, y: targetY }));
                } catch {
                  /* ignore */
                }
              } else if (prev && !next) {
                setPosition(computeInitialPosition());
              }
              return next;
            });
          }}
          sx={{ ml: 1, textTransform: "none" }}
        >
          {expanded ? "Minimizar" : "Expandir"}
        </Button>
      </Box>

      {expanded && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, px: 0.5, flexShrink: 0 }}>
            {!sessionId ? (
              <Typography variant="body2" color="text.secondary">
                {primarySkillKey === "ops_intelligence" ? (
                  <>
                    Este painel <b>não recebe</b> o relatório automático. Use <b>Testar agora</b> na
                    configuração: o briefing vai para o <b>chat da Equipe</b> com o agente (e WhatsApp/e-mail
                    se configurado). Depois volte aqui ou no chat da equipe para follow-up.
                  </>
                ) : (
                  <>
                    Ao iniciar o teste, o simulador cria uma <b>solicitação</b> de simulação atribuída a{" "}
                    <b>{displayAgentName}</b>. Você verá o protocolo e poderá abrir em popup para acompanhar o histórico
                    completo. Se o agente transferir (humano, fila, outro agente ou chatbot), o teste encerra — use{" "}
                    <b>Limpar</b> para simular de novo.
                  </>
                )}
              </Typography>
            ) : (
              <>
                <Tooltip title={`Abrir solicitação ${protocol} em popup`}>
                  <Chip
                    clickable
                    onClick={openPopupCall}
                    label={`Protocolo: ${protocol}`}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      maxWidth: 160,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  />
                </Tooltip>
                <IconButton size="small" onClick={openPopupCall} title="Abrir solicitação em popup" sx={{ ml: -0.5 }}>
                  <OpenInNewIcon fontSize="inherit" />
                </IconButton>
                {requestStatus !== null && requestStatus !== undefined && (
                  <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                    <StatusChipSelector
                      currentStatus={requestStatus}
                      onStatusChange={handleStatusChange}
                      size="small"
                    />
                    {loadingRequest && (
                      <Typography variant="caption" color="text.secondary">
                        Atualizando…
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>

          {agentActive === false && (
            <Alert
              severity="warning"
              sx={{ mb: 1, py: 0.5, px: 1, "& .MuiAlert-message": { fontSize: "0.8rem" } }}
            >
              {t("agentsListPage.simulatorSkipInactive", {
                defaultValue:
                  "Este agente está inativo, por isso não responde no teste nem nas solicitações reais.\nAtive o interruptor «Agente de IA ativo?» no topo desta tela, salve o agente e teste de novo.",
              })}
            </Alert>
          )}

          <Box
            ref={messagesContainerRef}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              mb: 1,
              px: 0.5,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": { bgcolor: "divider", borderRadius: 3 },
            }}
          >
            {messages.map((m, idx) => {
              const alignment = m.from === "user" ? "flex-end" : "flex-start";
              const isUser = m.from === "user";
              const hasText = m.text != null && String(m.text).trim() !== "";
              const hasAttachment = Boolean(m.attachment?.url);
              const hasButtons = Array.isArray(m.buttons) && m.buttons.length > 0;
              const hasLinkButtons = Array.isArray(m.linkButtons) && m.linkButtons.length > 0;
              const legacyImageUrl = m.imageUrl && !hasAttachment;
              const legacyFileUrl = m.fileUrl && !legacyImageUrl && !hasAttachment;

              if (!hasText && !hasAttachment && !legacyImageUrl && !legacyFileUrl && !hasButtons && !hasLinkButtons) {
                return null;
              }

              const bubbleBg = isUser
                ? "primary.main"
                : m.from === "bot"
                  ? "grey.200"
                  : m.kind === "transfer"
                    ? "success.light"
                    : "warning.light";
              const bubbleColor = isUser ? "primary.contrastText" : "text.primary";

              return (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: alignment,
                    mt: 0.5,
                  }}
                >
                  {m.from === "bot" && m.agentName && (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, px: 0.5 }}>
                      {m.agentName}
                    </Typography>
                  )}
                  {(hasText || hasAttachment || legacyImageUrl || legacyFileUrl) && (
                    <Box
                      sx={{
                        maxWidth: "85%",
                        px: hasAttachment && !hasText ? 0.75 : 1.5,
                        py: hasAttachment && !hasText ? 0.75 : 0.75,
                        borderRadius: 2,
                        bgcolor: bubbleBg,
                        color: bubbleColor,
                        fontSize: "0.85rem",
                        overflow: "hidden",
                      }}
                    >
                      {hasText && (
                        <Box sx={{ whiteSpace: "pre-line", mb: hasAttachment ? 0.75 : 0 }}>{m.text}</Box>
                      )}
                      {hasAttachment && (
                        <AgentSimulatorAttachment attachment={m.attachment} isOwn={isUser} />
                      )}
                      {legacyImageUrl && (
                        <AgentSimulatorAttachment
                          attachment={{ kind: "image", url: m.imageUrl, fileName: m.text || "Imagem" }}
                          isOwn={isUser}
                        />
                      )}
                      {legacyFileUrl && (
                        <AgentSimulatorAttachment
                          attachment={{ url: m.fileUrl, fileName: m.text || "Anexo" }}
                          isOwn={isUser}
                        />
                      )}
                    </Box>
                  )}

                  {(hasButtons || hasLinkButtons) && m.from === "bot" ? (
                    <Box
                      sx={{
                        width: "85%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        mt: hasText || hasAttachment ? 0.5 : 0,
                      }}
                    >
                      {m.buttons.map((b, bIdx) => {
                        const opt = Array.isArray(m.buttonOptions) ? m.buttonOptions[bIdx] : null;
                        const optionId = String(opt?.id || "").trim();
                        const label = typeof b === "object" ? b?.label || b?.title || optionId : b;
                        return (
                        <Button
                          key={`${idx}-reply-${bIdx}-${label}`}
                          variant="outlined"
                          size="small"
                          onClick={() => handleQuickReplyClick(label, optionId)}
                          disabled={sending || inputLocked}
                          sx={{
                            textTransform: "none",
                            justifyContent: "center",
                            borderRadius: 2,
                            bgcolor: "background.paper",
                          }}
                        >
                          {label}
                        </Button>
                        );
                      })}
                      {m.linkButtons.map((linkBtn, bIdx) => (
                        <Button
                          key={`${idx}-link-${bIdx}-${linkBtn.url}`}
                          variant="outlined"
                          size="small"
                          href={linkBtn.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                          sx={{
                            textTransform: "none",
                            justifyContent: "center",
                            borderRadius: 2,
                            bgcolor: "background.paper",
                          }}
                        >
                          {linkBtn.label || "Abrir link"}
                        </Button>
                      ))}
                    </Box>
                  ) : null}
                </Box>
              );
            })}
            {sending && <SimulatorTypingBubble label="Gerando resposta…" />}
            {messages.length === 0 && !sending && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Digite uma mensagem abaixo para testar o agente <b>{displayAgentName}</b>. As respostas usam o mesmo
                motor de IA aplicado às conversas reais (skills, memória e ferramentas configuradas).
              </Typography>
            )}
          </Box>

          {showTicketSellerShortcuts && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1, flexShrink: 0 }}>
              <Chip
                label="Falar com atendente"
                size="small"
                clickable
                variant="outlined"
                onClick={() => handleQuickReplyClick("Falar com atendente")}
                disabled={sending || inputLocked || !agentId}
              />
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", mt: 0.5, flexShrink: 0, gap: 0.5 }}>
            {transferred && (
              <Alert severity="success" sx={{ py: 0.5, px: 1, "& .MuiAlert-message": { fontSize: "0.8rem" } }}>
                {t("agentsListPage.simulatorTransferredHint", {
                  defaultValue: "Este teste encerrou. Clique em Limpar para começar outro.",
                })}
              </Alert>
            )}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextField
                inputRef={inputRef}
                size="small"
                placeholder={
                  transferred
                    ? t("agentsListPage.simulatorInputLockedTransfer", {
                        defaultValue:
                          "Solicitação transferida — clique em Limpar para outro teste.",
                      })
                    : isClosedRequest
                    ? "Solicitação encerrada — não é possível enviar mensagens."
                    : sending
                      ? "Aguardando a resposta…"
                      : "Digite uma mensagem..."
                }
                fullWidth
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={!agentId || sending || inputLocked}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (!inputLocked && !sending) handleSend();
                  }
                }}
              />
              <IconButton
                sx={{ ml: 1 }}
                color="primary"
                onClick={handleSend}
                disabled={!agentId || sending || inputLocked || !input.trim()}
              >
                <SendIcon />
              </IconButton>
            </Box>
            {sending && (
              <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, fontStyle: "italic" }}>
                Mensagem enviada. Aguardando resposta do agente…
              </Typography>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
}
